import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {
    FunctionFeature,
    ISheetAPI,
    SheetType,
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';

export class ArrayFormulaFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'array-formula';
    }

    @Function({
        title: 'ARRAYFORMULA(array_formula, column, sheet)',
        tip: 'Enables the display of values returned from an array formula into multiple rows and/or columns and the use of non-array functions with arrays.',
        alias: 'ARRAYFORMULA',
        example: 'ARRAYFORMULA(A1:C1+A2:C2)',
        usesFeatures: [FunctionFeature.ArrayFormula],
        returns:
            {
                title: 'first item of array',
                tip: 'Returns first item of array',
                type: SheetType.AnyIValue
            }
    })
    public async arrayFormula(@Param({
                                title: 'sheetAPI',
                                tip: 'API',
                                type: SheetType.SheetAPI
                            }) api:ISheetAPI,
                            @Param({
                                title: 'array_formula',
                                tip: 'A range, mathematical expression using one cell range or multiple ranges of the same size, or a function that returns a result greater than one cell.',
                                type: SheetType.AnyIValue
                            }) array_formula:IValue,
                            @Param({
                                title: 'num_rows',
                                tip: 'The number of rows the result should contain.',
                                type: Type.Integer,
                                optional: true,
                            }) num_rows?:AInteger,
                            @Param({
                                title: 'num_cols',
                                tip: 'The number of columns the result should contain.',
                                type: Type.Integer,
                                optional: true,
                            }) num_cols?:AInteger,
                            @Param({
                                title: 'extract_values',
                                tip: 'Extract values from frame. Default values is TRUE.',
                                type: Type.Boolean,
                                optional: true,
                            }) extract_values?:ABoolean):Promise<IValue | AError>
    {
        const extractValues = (extract_values && Type.isInstanceOf(extract_values, Type.Boolean)) ? extract_values.value : true;
        if (!extractValues)
        {
            return array_formula;
        }

        if( !Type.isInstanceOf(array_formula, Type.Frame) )
        {
            return array_formula;
        }

        let result:IValue = AEmpty.Value;

        const frame = <IFrame>array_formula;

        const num_rows_value = (Type.isInstanceOf(num_rows, Type.Integer)) ? Math.max(1, num_rows.value) : frame.recordCount;
        const num_cols_value = (Type.isInstanceOf(num_cols, Type.Integer)) ? Math.max(1, num_cols.value) : frame.fieldCount;

        const recordCount:number = Math.min(num_rows_value, frame.recordCount);
        const fieldCount:number = Math.min(num_cols_value, frame.fieldCount);

        const sheetContext = api.getSheetContext();
        const cellLocation = sheetContext.getCellLocation();

        for( let recordIndex = 0; recordIndex < recordCount; recordIndex++ )
        {
            for( let fieldIndex = 0; fieldIndex < fieldCount; fieldIndex++ )
            {
                let cellValue:IValue | null = frame.getCellByOffset(fieldIndex, recordIndex);

                if( !Type.isInstanceOfIValue(cellValue) )
                    cellValue = AEmpty.Value;

                // current cell result
                if( recordIndex == 0 && fieldIndex == 0 )
                {
                    result = cellValue;
                }
                else
                {
                    const sheetLocation = new SheetLocationImpl(cellLocation.sheetId, cellLocation.columnIndex + fieldIndex, cellLocation.rowIndex + recordIndex);
                    await sheetContext.setCellFormula(sheetLocation, cellValue);
                }
            }
        }

        return result;
    }
}
